import React, { useEffect, useMemo, useState } from "react";
import axios from "./axiosConfig";
import { AuthProvider, useAuth } from "./AuthContext";
import { LoadingProvider, useLoading } from "./LoadingContext";
import { sendPasswordResetEmail } from "firebase/auth";
import { auth } from "./firebase";
import Auth from "./Auth";
import Predictions from "./Predictions";
import Admin from "./Admin";
import ChevronToggleButton from "./ChevronToggleButton";
import ManageLeaguesModal from "./ManageLeaguesModal";
import LoadingSpinner from "./LoadingSpinner";
import Legal from "./Legal";
import { setSentryUser } from "./sentry";
import "./App.css";

// #region Constants: Supported competitions and fixture status groups used by timing logic.
const COMPETITIONS = [
  { id: 39, label: "Premier League" },
  { id: 1, label: "World Cup" },
  { id: 2, label: "Champions League" },
];

const THEME_OPTIONS = [
  { id: "retro", label: "Retro Arcade" },
  { id: "sunset", label: "Sunset Sport" },
  { id: "neon", label: "Neon Pitch" },
  { id: "dark", label: "Dark" },
];
const DEFAULT_THEME = "sunset";
const SUPPORT_EMAIL =
  process.env.REACT_APP_SUPPORT_EMAIL || "support@matchmixer.co.uk";

const FINISHED_STATUSES = new Set(["FT", "FINISHED", "AET", "PEN"]);
const IN_PROGRESS_STATUSES = new Set([
  "IN_PLAY",
  "LIVE",
  "1H",
  "2H",
  "HT",
  "ET",
  "BT",
  "PAUSED",
]);
const BLOCKED_SCORE_STATUSES = new Set([
  "POSTPONED",
  "SUSPENDED",
  "CANCELLED",
  "PST",
  "CANC",
]);
// #endregion

// #region Helper Functions: Shared date/status utilities and matchday timing formatters.
const getRoundSortValue = (roundLabel) => {
  const matches = String(roundLabel).match(/(\d+)/g);
  if (!matches || matches.length === 0) return Number.MAX_SAFE_INTEGER;
  return Number(matches[matches.length - 1]);
};

const isFixtureFinished = (status) =>
  FINISHED_STATUSES.has(String(status || ""));

const isFixtureInProgress = (status) =>
  IN_PROGRESS_STATUSES.has(String(status || ""));

const isFixtureBlockedForScore = (status) =>
  BLOCKED_SCORE_STATUSES.has(String(status || ""));

const hasLiveFixtureInGameweek = (gameweek) => {
  const fixtures = gameweek?.fixtures || [];
  if (fixtures.length === 0) return false;

  return fixtures.some((fixture) => isFixtureInProgress(fixture.statusShort));
};

const hasRemainingPlayableFixture = (gameweek) => {
  const fixtures = gameweek?.fixtures || [];
  if (fixtures.length === 0) return false;

  return fixtures.some(
    (fixture) =>
      !isFixtureFinished(fixture.statusShort) &&
      !isFixtureBlockedForScore(fixture.statusShort),
  );
};

const resolveCurrentGameweekIndex = (sortedGameweeks, nowTs) => {
  if (!Array.isArray(sortedGameweeks) || sortedGameweeks.length === 0) {
    return -1;
  }

  const liveIndex = sortedGameweeks.findIndex((gameweek) =>
    hasLiveFixtureInGameweek(gameweek),
  );
  if (liveIndex >= 0) return liveIndex;

  // Prefer the earliest gameweek that still has playable fixtures left.
  const remainingPlayableIndex = sortedGameweeks.findIndex((gameweek) =>
    hasRemainingPlayableFixture(gameweek),
  );
  if (remainingPlayableIndex >= 0) return remainingPlayableIndex;

  const nextIndex = sortedGameweeks.findIndex((gameweek) => {
    if (!gameweek.firstKickoff) return false;
    const kickoffTs = new Date(gameweek.firstKickoff).getTime();
    return Number.isFinite(kickoffTs) && kickoffTs > nowTs;
  });

  if (nextIndex === -1) {
    return sortedGameweeks.length - 1;
  }

  if (nextIndex === 0) {
    return 0;
  }

  return nextIndex;
};

const getGameweekLastKickoff = (gameweek) => {
  const fixtureTimes = (gameweek?.fixtures || [])
    .map((fixture) => new Date(fixture.date).getTime())
    .filter(Number.isFinite);

  if (fixtureTimes.length === 0) {
    return gameweek?.firstKickoff || null;
  }

  return new Date(Math.max(...fixtureTimes)).toISOString();
};

const formatDateTime = (value) => {
  if (!value) return "TBC";
  const ts = new Date(value).getTime();
  if (!Number.isFinite(ts)) return "TBC";
  return new Date(value).toLocaleString(undefined, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatTime = (value) => {
  if (!value) return "TBC";
  const ts = new Date(value).getTime();
  if (!Number.isFinite(ts)) return "TBC";
  return new Date(value).toLocaleString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatCountdown = (value, nowTs) => {
  if (!value) return "TBC";
  const targetTs = new Date(value).getTime();
  if (!Number.isFinite(targetTs)) return "TBC";

  const diffMs = targetTs - nowTs;
  const diffMinutes = Math.max(0, Math.floor(Math.abs(diffMs) / 60000));
  const days = Math.floor(diffMinutes / (60 * 24));
  const hours = Math.floor((diffMinutes % (60 * 24)) / 60);
  const minutes = diffMinutes % 60;

  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0 || parts.length === 0) parts.push(`${minutes}m`);

  return diffMs >= 0 ? `in ${parts.join(" ")}` : `${parts.join(" ")} ago`;
};
// #endregion

function App() {
  const { logout, user } = useAuth();
  const { isLoading, incrementRequestCount, decrementRequestCount } =
    useLoading();
  // #region State: Local UI/data state for profile, timing, and league create/join controls.
  const [profile, setProfile] = useState(null);
  const [showAdmin, setShowAdmin] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [error, setError] = useState(null);
  const [legalPage, setLegalPage] = useState(null);
  const [activeCompetitionId, setActiveCompetitionId] = useState(() => {
    const saved = localStorage.getItem("mm_competitionId");
    return saved ? Number(saved) : null;
  });
  const [gameweeks, setGameweeks] = useState([]);
  const [isLoadingGameweeks, setIsLoadingGameweeks] = useState(true);
  const [nowTs, setNowTs] = useState(() => Date.now());
  const [leagueActionMessage, setLeagueActionMessage] = useState("");
  const [isLeagueActionBusy, setIsLeagueActionBusy] = useState(false);
  const [createLeagueName, setCreateLeagueName] = useState("");
  const [createLeagueCompetitionId, setCreateLeagueCompetitionId] =
    useState("");
  const [createLeagueFormat, setCreateLeagueFormat] = useState("");
  const [createLeagueVisibility, setCreateLeagueVisibility] = useState("");
  const [ownedLeagues, setOwnedLeagues] = useState([]);
  const [isLoadingOwnedLeagues, setIsLoadingOwnedLeagues] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [leaguesRefreshSignal, setLeaguesRefreshSignal] = useState(0);
  const [expandedLeagueActionTab, setExpandedLeagueActionTab] = useState(null);
  const [howToPlayMode, setHowToPlayMode] = useState("score");
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileDisplayName, setProfileDisplayName] = useState("");
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [profileEditMessage, setProfileEditMessage] = useState("");
  const [profileEditError, setProfileEditError] = useState("");
  const [isPasswordResetSent, setIsPasswordResetSent] = useState(false);
  const [passwordResetError, setPasswordResetError] = useState("");
  const [notifToggleBusy, setNotifToggleBusy] = useState(false);
  const [notifToggleError, setNotifToggleError] = useState("");
  const [isProfileCollapsed, setIsProfileCollapsed] = useState(true);
  const [manageLeaguesOpen, setManageLeaguesOpen] = useState(false);
  const [isFirstSetupModal, setIsFirstSetupModal] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState(() => {
    const storedTheme = window.localStorage.getItem("finalScoreTheme");
    if (
      storedTheme &&
      THEME_OPTIONS.some((theme) => theme.id === storedTheme)
    ) {
      return storedTheme;
    }
    return DEFAULT_THEME;
  });
  // #endregion

  // #region UI Actions: Small interaction helpers for tab toggling.
  const toggleLeagueActionTab = (tab) => {
    setExpandedLeagueActionTab((prev) => (prev === tab ? null : tab));
    setIsEditingProfile(false);
    setManageLeaguesOpen(false);
  };
  // #endregion

  // #region Effects: Setup axios interceptors for loading state tracking.
  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        incrementRequestCount();
        return config;
      },
      (error) => {
        decrementRequestCount();
        return Promise.reject(error);
      },
    );

    const responseInterceptor = axios.interceptors.response.use(
      (response) => {
        decrementRequestCount();
        return response;
      },
      (error) => {
        decrementRequestCount();
        return Promise.reject(error);
      },
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [incrementRequestCount, decrementRequestCount]);
  // #endregion

  // #region Effects: Data fetching and timer updates for profile and competition gameweeks.
  useEffect(() => {
    axios
      .get("/api/v1/user/profile")
      .then((res) => setProfile(res.data))
      .catch((err) => setError("Could not fetch profile"));
  }, []);

  useEffect(() => {
    if (!profile?.username) return;
    setProfileDisplayName(profile.username);
  }, [profile?.username]);

  useEffect(() => {
    if (!profile?.id) return;
    axios.get("/api/v1/leagues/global-competitions").then((res) => {
      const comps = res.data?.competitions || [];
      // Guard against WC-only members slipping through: only count competitions with upcoming fixtures.
      const activeComps = comps.filter((c) => c.hasUpcomingFixtures !== false);
      const isMissingFromAnyActive = activeComps.length > 0 && activeComps.some((c) => !c.isMember);
      if (isMissingFromAnyActive) {
        setIsFirstSetupModal(true);
        setManageLeaguesOpen(true);
      }
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id]);

  useEffect(() => {
    if (!profile?.id) return;
    // On page refresh, sessionStorage flag is already set — skip API, keep localStorage value.
    // On fresh login (or after logout), flag is absent — always call API for most relevant matchday.
    if (sessionStorage.getItem("mm_session_init") === "true") return;
    axios
      .get("/api/v1/user/next-matchday")
      .then((res) => {
        const { competitionId } = res.data || {};
        setActiveCompetitionId(Number(competitionId) || 39);
      })
      .catch(() => setActiveCompetitionId(39))
      .finally(() => sessionStorage.setItem("mm_session_init", "true"));
  }, [profile?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeCompetitionId !== null) {
      localStorage.setItem("mm_competitionId", String(activeCompetitionId));
    }
  }, [activeCompetitionId]);

  useEffect(() => {
    if (activeCompetitionId === null) return;
    const fetchGameweeks = async () => {
      setIsLoadingGameweeks(true);
      try {
        const res = await axios.get("/api/v1/gameweeks", {
          params: { league: activeCompetitionId },
        });
        const gameweekData = Array.isArray(res.data)
          ? res.data
          : res.data?.gameweeks || [];
        setGameweeks(gameweekData);
      } catch (fetchError) {
        setGameweeks([]);
      } finally {
        setIsLoadingGameweeks(false);
      }
    };

    fetchGameweeks();
  }, [activeCompetitionId]);

  useEffect(() => {
    if (!profile?.id) return;

    const loadOwnedLeagues = async () => {
      setIsLoadingOwnedLeagues(true);
      try {
        const res = await axios.get("/api/v1/leagues");
        const myLeagues = (res.data?.leagues || []).filter(
          (league) => league.ownerUsername === profile.id,
        );
        setOwnedLeagues(myLeagues);
      } catch (loadError) {
        setOwnedLeagues([]);
      } finally {
        setIsLoadingOwnedLeagues(false);
      }
    };

    loadOwnedLeagues();
  }, [profile?.id, leaguesRefreshSignal]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setNowTs(Date.now());
    }, 60000);

    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const onScroll = () => setShowBackToTop(window.scrollY > 400);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", selectedTheme);
    window.localStorage.setItem("finalScoreTheme", selectedTheme);

    const themeColorByTheme = {
      retro: "#ff2f92",
      sunset: "#ff4f8b",
      neon: "#39ff78",
      dark: "#0f1220",
    };
    const themeColorMeta = document.querySelector("meta[name='theme-color']");
    if (themeColorMeta) {
      themeColorMeta.setAttribute(
        "content",
        themeColorByTheme[selectedTheme] || themeColorByTheme[DEFAULT_THEME],
      );
    }
  }, [selectedTheme]);
  // #endregion

  // #region Derived Values: Sorted gameweeks, labels, and computed timing panel values.
  const sortedGameweeks = useMemo(
    () =>
      [...gameweeks].sort((a, b) => {
        const aTime = a.firstKickoff
          ? new Date(a.firstKickoff).getTime()
          : Number.MAX_SAFE_INTEGER;
        const bTime = b.firstKickoff
          ? new Date(b.firstKickoff).getTime()
          : Number.MAX_SAFE_INTEGER;
        if (aTime !== bTime) return aTime - bTime;

        const aRound = getRoundSortValue(a.id);
        const bRound = getRoundSortValue(b.id);
        return aRound - bRound;
      }),
    [gameweeks],
  );

  const activeCompetitionLabel =
    COMPETITIONS.find((competition) => competition.id === activeCompetitionId)
      ?.label || "Selected competition";

  const matchdayTiming = useMemo(() => {
    if (!sortedGameweeks.length) {
      return null;
    }

    const activeIndex = resolveCurrentGameweekIndex(sortedGameweeks, nowTs);

    const hasUpcoming = sortedGameweeks.some((gw) => {
      const kickoffTs = new Date(gw.firstKickoff || 0).getTime();
      return Number.isFinite(kickoffTs) && kickoffTs > nowTs;
    });

    if (activeIndex >= 0) {
      const currentGameweek = sortedGameweeks[activeIndex];
      const nextGameweek = sortedGameweeks[activeIndex + 1] || null;
      const currentBadgeId = String(currentGameweek?.id || "Current");
      const currentFirstTime = currentGameweek?.firstKickoff || null;
      const currentDeadline = currentFirstTime
        ? new Date(new Date(currentFirstTime).getTime() - 30 * 60 * 1000).toISOString()
        : null;
      const currentTime = getGameweekLastKickoff(currentGameweek);
      const nextTime = nextGameweek?.firstKickoff || null;
      return {
        currentBadge: hasUpcoming ? `${currentBadgeId} - Current` : currentBadgeId,
        currentFirstTime,
        currentDeadline,
        currentTime,
        currentCountdown: formatCountdown(currentTime, nowTs),
        nextBadge: nextGameweek?.id || "Next",
        nextLabel: "Kickoff",
        nextTime,
        nextCountdown: formatCountdown(nextTime, nowTs),
      };
    }

    const nextIndex = sortedGameweeks.findIndex((gameweek) => {
      if (!gameweek.firstKickoff) return false;
      return new Date(gameweek.firstKickoff).getTime() >= nowTs;
    });

    if (nextIndex >= 0) {
      const previousGameweek =
        nextIndex > 0 ? sortedGameweeks[nextIndex - 1] : null;
      const nextGameweek = sortedGameweeks[nextIndex];
      const currentFirstTime = previousGameweek?.firstKickoff || null;
      const currentTime = previousGameweek
        ? getGameweekLastKickoff(previousGameweek)
        : null;
      const nextTime = nextGameweek.firstKickoff || null;
      return {
        currentBadge: previousGameweek?.id || "Previous",
        currentFirstTime,
        currentTime,
        currentCountdown: formatCountdown(currentTime, nowTs),
        nextBadge: nextGameweek.id,
        nextLabel: "Kickoff",
        nextTime,
        nextCountdown: formatCountdown(nextTime, nowTs),
      };
    }

    const lastGameweek = sortedGameweeks[sortedGameweeks.length - 1];
    const currentFirstTime = lastGameweek?.firstKickoff || null;
    const currentTime = getGameweekLastKickoff(lastGameweek);
    return {
      currentBadge: lastGameweek.id,
      currentFirstTime,
      currentTime,
      currentCountdown: formatCountdown(currentTime, nowTs),
      nextBadge: "Next",
      nextLabel: "Starts",
      nextTime: null,
      nextCountdown: "TBC",
    };
  }, [nowTs, sortedGameweeks]);

  const renderCompetitionSelect = () => (
    <select
      className="gameweek-select competition-select"
      value={activeCompetitionId ?? ""}
      onChange={(e) => setActiveCompetitionId(Number(e.target.value))}
      aria-label="Select competition"
      disabled={activeCompetitionId === null}
    >
      {COMPETITIONS.map((competition) => (
        <option key={competition.id} value={competition.id}>
          {competition.label}
        </option>
      ))}
    </select>
  );
  // #endregion

  // #region Event Handlers: Create/join league submit flows and result messaging.
  const handleCreateLeague = async (e) => {
    e.preventDefault();
    const trimmedName = createLeagueName.trim();
    if (!trimmedName) {
      setLeagueActionMessage("Enter a league name.");
      return;
    }
    if (
      !createLeagueCompetitionId ||
      !createLeagueFormat ||
      !createLeagueVisibility
    ) {
      setLeagueActionMessage(
        "Pick a competition, game format, and league type.",
      );
      return;
    }

    setIsLeagueActionBusy(true);
    setLeagueActionMessage("");

    try {
      const username = profile?.id;
      if (!username) {
        setLeagueActionMessage(
          "Profile is still loading. Try again in a moment.",
        );
        return;
      }
      const response = await axios.post("/api/v1/leagues", {
        username,
        name: trimmedName,
        competitionId: Number(createLeagueCompetitionId),
        visibility: createLeagueVisibility,
        gameFormat: createLeagueFormat,
      });

      const createdLeague = response.data?.league;
      const joinCode = createdLeague?.joinCode;

      setCreateLeagueName("");
      setCreateLeagueCompetitionId("");
      setCreateLeagueFormat("");
      setCreateLeagueVisibility("");
      setLeagueActionMessage(
        `League created: ${createdLeague?.name || trimmedName}. ${joinCode ? `Share code: ${joinCode}. ` : ""}Join with the league code.`,
      );
      setExpandedLeagueActionTab("codes");
      setLeaguesRefreshSignal((prev) => prev + 1);
    } catch (err) {
      if (err?.response?.status === 409) {
        setLeagueActionMessage(
          "You already created a league with this name in this competition. Pick a different name.",
        );
        return;
      }
      const apiMessage = err?.response?.data?.message;
      setLeagueActionMessage(apiMessage || "Could not create league.");
    } finally {
      setIsLeagueActionBusy(false);
    }
  };

  const handleCopyLeagueCode = (code) => {
    if (!code) return;
    navigator.clipboard
      .writeText(code)
      .then(() => {
        setToastMessage("Code copied to clipboard!");
        setTimeout(() => setToastMessage(""), 2000);
      })
      .catch(() => {
        setLeagueActionMessage("Could not copy code. Please try again.");
      });
  };

  const handleShareLeagueInvite = async (league) => {
    const joinCode = league?.joinCode;
    if (!joinCode) return;

    const appUrl = window.location.origin;
    const inviteText = `Join my ${league.name} league on Match Mixer.\nCode: ${joinCode}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: "Join my Match Mixer league",
          text: inviteText,
          url: appUrl,
        });
        setToastMessage("Share opened.");
        setTimeout(() => setToastMessage(""), 2000);
        return;
      } catch (error) {
        if (error?.name === "AbortError") {
          return;
        }
      }
    }

    try {
      await navigator.clipboard.writeText(inviteText);
      setToastMessage("Invite copied. Share it with your friend.");
      setTimeout(() => setToastMessage(""), 2200);
    } catch {
      setLeagueActionMessage("Could not share invite. Please try again.");
    }
  };

  const handleProfileSave = async (e) => {
    e.preventDefault();
    const trimmedDisplayName = profileDisplayName.trim();
    if (!trimmedDisplayName) {
      setProfileEditError("Display name is required.");
      return;
    }
    if (trimmedDisplayName.length < 2) {
      setProfileEditError("Display name must be at least 2 characters.");
      return;
    }

    setIsSavingProfile(true);
    setProfileEditError("");
    setProfileEditMessage("");

    try {
      const response = await axios.put("/api/v1/user/profile", {
        displayName: trimmedDisplayName,
      });
      const updatedUser = response.data?.user || {};
      setProfile((prev) => ({
        ...prev,
        username: updatedUser.username || trimmedDisplayName,
        email: updatedUser.email || prev?.email,
      }));
      setProfileEditMessage("Profile updated.");
      setIsEditingProfile(false);
      setLeaguesRefreshSignal((prev) => prev + 1);
    } catch (err) {
      const apiMessage = err?.response?.data?.message;
      setProfileEditError(apiMessage || "Could not update profile.");
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleProfileEditToggle = () => {
    setIsEditingProfile((prev) => !prev);
    setExpandedLeagueActionTab(null);
    setManageLeaguesOpen(false);
    setProfileEditError("");
    setProfileEditMessage("");
    setIsPasswordResetSent(false);
    setPasswordResetError("");
    setProfileDisplayName(profile?.username || "");
  };

  const handleEmailSubscriptionToggle = async () => {
    if (notifToggleBusy || !profile) return;
    setNotifToggleBusy(true);
    setNotifToggleError("");
    const endpoint = profile.emailUnsubscribed ? "/api/v1/user/resubscribe" : "/api/v1/user/unsubscribe";
    try {
      const res = await axios.post(endpoint);
      setProfile((prev) => ({ ...prev, emailUnsubscribed: res.data.emailUnsubscribed }));
    } catch {
      setNotifToggleError("Could not update email preference. Please try again.");
    } finally {
      setNotifToggleBusy(false);
    }
  };

  const handlePasswordReset = async () => {
    setIsPasswordResetSent(false);
    setPasswordResetError("");
    try {
      await sendPasswordResetEmail(auth, user.email);
      setIsPasswordResetSent(true);
    } catch (err) {
      setPasswordResetError("Failed to send reset email. Please try again.");
    }
  };

  const handleProfileCollapseToggle = () => {
    setIsProfileCollapsed((prev) => {
      const nextCollapsed = !prev;
      if (nextCollapsed) {
        setIsEditingProfile(false);
        setExpandedLeagueActionTab(null);
      }
      return nextCollapsed;
    });
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (logoutError) {
      setError("Could not log out. Please try again.");
    }
  };
  // #endregion

  // #region Render: Main app layout, profile/timing panel, and embedded game component.
  return (
    <div className="App">
      <LoadingSpinner isVisible={isLoading} />
      {toastMessage && <div className="copy-toast">{toastMessage}</div>}
      <main className="app-main">
        <div className="app-header-row">
          <div className="app-header-brand">
            <img
              src={`${process.env.PUBLIC_URL}/Logo/logo-no-text.svg`}
              alt="Match Mixer"
              className="app-header-logo app-header-logo--desktop"
            />
            <img
              src={`${process.env.PUBLIC_URL}/Logo/logo-no-text.svg`}
              alt="Match Mixer"
              className="app-header-logo app-header-logo--mobile"
            />
            <h1 className="app-header-title">Match Mixer</h1>
          </div>
          <div className="app-header-controls">
            {profile?.isAdmin && (
              <button
                className="league-action-tab admin-header-btn"
                onClick={() => setShowAdmin((prev) => !prev)}
              >
                {showAdmin ? "Hide Admin" : "Admin"}
              </button>
            )}
            {profile && (
              <button
                className="league-action-tab"
                onClick={handleLogout}
              >
                Logout
              </button>
            )}
            <section
              className="theme-switchboard"
              aria-label="Mode switchboard"
            >
              <p className="theme-switchboard-label">Mode</p>
              <div className="theme-switchboard-options">
                {THEME_OPTIONS.map((theme) => (
                  <button
                    key={theme.id}
                    type="button"
                    aria-label={theme.label}
                    aria-pressed={selectedTheme === theme.id}
                    title={theme.label}
                    className={`theme-switch-btn ${`theme-switch-btn-${theme.id}`} ${
                      selectedTheme === theme.id ? "active" : ""
                    }`}
                    onClick={() => setSelectedTheme(theme.id)}
                  />
                ))}
              </div>
            </section>
          </div>
        </div>
        {error && <p className="error-text">{error}</p>}

        {profile?.isAdmin && showAdmin && (
          <Admin onClose={() => setShowAdmin(false)} />
        )}

        {profile ? (
          <>
            <section className="profile-card">
              <div className="profile-card-header">
                <h2>{profile.username}</h2>
                <div className="profile-card-controls">
                  <ChevronToggleButton
                    isExpanded={!isProfileCollapsed}
                    className="profile-collapse-toggle"
                    onClick={handleProfileCollapseToggle}
                    expandedLabel="Collapse player card"
                    collapsedLabel="Expand player card"
                  />
                </div>
              </div>

              {!isProfileCollapsed && (
                <>
                  <div className="profile-summary">
                    <div className="profile-identity">
                      <p>
                        <strong>Email:</strong> {profile.email}
                      </p>
                      <div className="league-actions-tabs profile-inline-actions">
                        <button
                          type="button"
                          className={`league-action-tab ${
                            expandedLeagueActionTab === "create" ? "active" : ""
                          }`}
                          onClick={() => toggleLeagueActionTab("create")}
                        >
                          Create League
                        </button>
                        <button
                          type="button"
                          className={`league-action-tab ${
                            expandedLeagueActionTab === "codes" ? "active" : ""
                          }`}
                          onClick={() => toggleLeagueActionTab("codes")}
                        >
                          League Codes
                        </button>
                        <button
                          type="button"
                          className="league-action-tab"
                          onClick={() => {
                            setIsFirstSetupModal(false);
                            setManageLeaguesOpen(true);
                            setIsEditingProfile(false);
                            setExpandedLeagueActionTab(null);
                          }}
                        >
                          Manage Competitions
                        </button>
                        <button
                          type="button"
                          className={`league-action-tab profile-edit-toggle ${isEditingProfile ? "active" : ""}`}
                          onClick={handleProfileEditToggle}
                        >
                          {isEditingProfile ? "Close Edit" : "Edit Profile"}
                        </button>
                        <button
                          type="button"
                          className={`league-action-tab ${expandedLeagueActionTab === "howtoplay" ? "active" : ""}`}
                          onClick={() => toggleLeagueActionTab("howtoplay")}
                        >
                          How to Play
                        </button>
                        <button
                          type="button"
                          className={`league-action-tab ${expandedLeagueActionTab === "notifications" ? "active" : ""}`}
                          onClick={() => toggleLeagueActionTab("notifications")}
                        >
                          Notifications
                        </button>
                      </div>
                    </div>
                  </div>

                  {isEditingProfile && (
                    <section className="profile-edit-card">
                      <form
                        className="profile-edit-form"
                        onSubmit={handleProfileSave}
                      >
                        <label
                          className="field-label"
                          htmlFor="profile-display-name"
                        >
                          Display name
                        </label>
                        <div className="profile-field-row">
                          <input
                            id="profile-display-name"
                            className="league-input profile-field-input"
                            value={profileDisplayName}
                            onChange={(e) =>
                              setProfileDisplayName(e.target.value)
                            }
                            maxLength={40}
                            placeholder="Display name"
                          />
                          {!user?.providerData?.every(
                            (p) => p.providerId === "google.com",
                          ) &&
                            (isPasswordResetSent ? (
                              <p
                                className="helper-text league-action-message"
                                style={{ margin: 0 }}
                              >
                                Reset email sent!
                              </p>
                            ) : (
                              <button
                                type="button"
                                className="league-action-tab"
                                onClick={handlePasswordReset}
                              >
                                Reset Password via Email
                              </button>
                            ))}
                          {passwordResetError && (
                            <p
                              className="status-message lock-message"
                              style={{ margin: 0 }}
                            >
                              {passwordResetError}
                            </p>
                          )}
                        </div>

                        <label className="field-label" htmlFor="profile-email">
                          Email
                        </label>
                        <input
                          id="profile-email"
                          className="league-input profile-field-input"
                          value={profile.email || ""}
                          disabled
                        />

                        <div className="profile-edit-actions">
                          <button
                            type="submit"
                            className="submit-btn"
                            disabled={isSavingProfile}
                          >
                            {isSavingProfile ? "Saving..." : "Save"}
                          </button>
                          <button
                            type="button"
                            className="league-action-tab"
                            onClick={handleProfileEditToggle}
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    </section>
                  )}

                  {profileEditError && (
                    <p className="status-message lock-message">
                      {profileEditError}
                    </p>
                  )}
                  {profileEditMessage && (
                    <p className="helper-text league-action-message">
                      {profileEditMessage}
                    </p>
                  )}

                  {expandedLeagueActionTab && (
                    <section className="league-actions-card">
                      {expandedLeagueActionTab === "create" && (
                        <form
                          className="league-action-form"
                          onSubmit={handleCreateLeague}
                        >
                          <input
                            className="league-input"
                            placeholder="League name"
                            value={createLeagueName}
                            onChange={(e) =>
                              setCreateLeagueName(e.target.value)
                            }
                          />
                          <div className="league-inline-controls">
                            <select
                              className="gameweek-select"
                              value={createLeagueCompetitionId}
                              onChange={(e) =>
                                setCreateLeagueCompetitionId(e.target.value)
                              }
                            >
                              <option value="">Pick a competition</option>
                              {COMPETITIONS.map((competition) => (
                                <option
                                  key={competition.id}
                                  value={competition.id}
                                >
                                  {competition.label}
                                </option>
                              ))}
                            </select>
                            <select
                              className="gameweek-select"
                              value={createLeagueFormat}
                              onChange={(e) =>
                                setCreateLeagueFormat(e.target.value)
                              }
                            >
                              <option value="">Pick a game format</option>
                              <option value="score_prediction">
                                Score Predictor
                              </option>
                              <option value="last_one_standing">
                                Last One Standing
                              </option>
                            </select>
                          </div>
                          <div className="league-inline-controls">
                            <select
                              className="gameweek-select"
                              value={createLeagueVisibility}
                              onChange={(e) =>
                                setCreateLeagueVisibility(e.target.value)
                              }
                            >
                              <option value="">Pick a league type</option>
                              <option value="private">Private</option>
                              <option value="public">Public</option>
                            </select>
                            <button
                              type="submit"
                              className="submit-btn"
                              disabled={isLeagueActionBusy}
                            >
                              {isLeagueActionBusy ? "Working..." : "Create"}
                            </button>
                          </div>
                        </form>
                      )}

                      {expandedLeagueActionTab === "codes" && (
                        <div className="league-action-form league-code-drawer">
                          <p className="helper-text league-code-helper">
                            Share these codes with other players to join your
                            leagues.
                          </p>
                          {isLoadingOwnedLeagues ? (
                            <p className="helper-text">
                              Loading your league codes...
                            </p>
                          ) : ownedLeagues.length === 0 ? (
                            <p className="helper-text">
                              You do not own any leagues yet.
                            </p>
                          ) : (
                            <div className="league-code-list">
                              {ownedLeagues.map((league) => {
                                const competitionLabel =
                                  {
                                    39: "Premier League",
                                    1: "World Cup",
                                    2: "Champions League",
                                  }[league.competitionId] ||
                                  `Competition ${league.competitionId}`;
                                return (
                                  <div
                                    key={league.id}
                                    className="league-code-row"
                                  >
                                    <div className="league-code-meta">
                                      <strong>{league.name}</strong>
                                      <span className="helper-text">
                                        {competitionLabel} • {league.visibility}{" "}
                                        •{" "}
                                        {league.gameFormat ===
                                        "last_one_standing"
                                          ? "LOS"
                                          : "Score Predictor"}
                                      </span>
                                    </div>
                                    <div className="league-code-actions">
                                      <span className="league-code-pill">
                                        {league.joinCode || "No code"}
                                      </span>
                                      <button
                                        type="button"
                                        className="league-action-tab"
                                        disabled={!league.joinCode}
                                        onClick={() =>
                                          handleCopyLeagueCode(league.joinCode)
                                        }
                                      >
                                        Copy
                                      </button>
                                      <button
                                        type="button"
                                        className="league-action-tab"
                                        disabled={!league.joinCode}
                                        onClick={() =>
                                          handleShareLeagueInvite(league)
                                        }
                                      >
                                        Share
                                      </button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}

                      {expandedLeagueActionTab === "howtoplay" && (
                        <div className="league-action-form how-to-play-drawer">
                          <div className="htp-mode-tabs">
                            <button
                              type="button"
                              className={`league-action-tab${howToPlayMode === "score" ? " active" : ""}`}
                              onClick={() => setHowToPlayMode("score")}
                            >
                              Score Predictor
                            </button>
                            <button
                              type="button"
                              className={`league-action-tab${howToPlayMode === "los" ? " active" : ""}`}
                              onClick={() => setHowToPlayMode("los")}
                            >
                              Last One Standing
                            </button>
                          </div>

                          {howToPlayMode === "score" && (
                            <div className="htp-content">
                              <p className="htp-intro">Predict the score for every fixture in the matchday before the deadline.</p>
                              <ul className="game-rules-list">
                                <li> Get <span style={{ fontWeight: "bold" }}>3 pts</span>  for an exact score (e.g. you predict 2-1, result is 2-1) 
                                    or <span style={{ fontWeight: "bold" }}>1 pt</span>  for a correct result — right winner or draw but a different score.</li>
                                <li> No points awarded for a wrong result.</li>
                                <li> Predictions are locked 30 minutes before the first kick-off of the matchday. You can edit any time before that, as many times as you want.</li>
                                <li> Where applicable (i.e Knockout games), extra-time goals count toward the final score. 
                                    Penalty shootouts do not count towards the final score — a match that ends 1-1 after extra-time and goes to penalties is scored as 1-1. Predicting the winning team in a shootout earns no points - 
                                    penalties are considered a tie breaker and are not considered as part of the final score.</li>
                                <li> Points accumulate on the season leaderboard across all matchdays. Player with the most points at the end of the season wins.</li>
                                <li> In the event of a tie on points, the player with the most exact score predictions wins.</li>
                              </ul>
                            </div>
                          )}

                          {howToPlayMode === "los" && (
                            <div className="htp-content">
                              <p className="htp-intro">Pick just one team to win each matchday. Predicting a winning team keeps you in the game.</p>
                              <ul className="game-rules-list">
                                <li> Pick a team to win their match before the matchday deadline (30 minutes before matchday kickoff).</li>
                                <li> If your team wins, you survive to the next matchday - any other result eliminates you.</li>
                                <li> If all remaining players get their picks wrong on the same matchday, a new run automatically starts and every player is back in the competition.</li>
                                <li> A run is a series of matchdays where players are still active and will only end if there is a winner or all remaining players are eliminated.</li>
                                <li> You cannot pick a team twice in the same run but if a new run starts, you can pick any team.</li>
                                <li> If a match goes to a penalty shootout and you pick the team that wins on penalties, you will progress to the next round.</li>
                                <li> Miss the deadline without picking and you are eliminated.</li>
                                <li> The last player standing wins.</li>
                                <li> If a winner is declared before the end of the actual competition, the league creator can choose to start a new run so everyone can have another go!</li>
                                <li> If a new player joins the league whilst a run is active they will not be included until the current run ends and a new run begins.</li>
                              </ul>
                              
                            </div>
                          )}
                        </div>
                      )}
                      {expandedLeagueActionTab === "notifications" && (
                        <div className="league-action-form">
                          <p className="field-label" style={{ margin: "0 0 12px" }}>Email notifications</p>
                          <label className="notif-toggle-row">
                            <input
                              type="checkbox"
                              className="notif-toggle-checkbox"
                              checked={!profile.emailUnsubscribed}
                              disabled={notifToggleBusy}
                              onChange={handleEmailSubscriptionToggle}
                            />
                            <span className="notif-toggle-label">
                              Receive email notifications from Match Mixer
                            </span>
                          </label>
                          <p className="helper-text" style={{ marginTop: 8 }}>
                            Covers matchday reminders, results, and Last One Standing events.
                          </p>
                          {notifToggleError && (
                            <p className="status-message lock-message" style={{ marginTop: 8 }}>{notifToggleError}</p>
                          )}
                        </div>
                      )}
                    </section>
                  )}

                  {leagueActionMessage && (
                    <p className="helper-text league-action-message">
                      {leagueActionMessage}
                    </p>
                  )}
                </>
              )}
            </section>

            <section className="matchday-timing-card">
              <div className="card-heading-row">
                <h2>Matchday Timing</h2>
                {renderCompetitionSelect()}
              </div>
              <div className="matchday-timing-panel">
                <p className="matchday-timing-title">
                  {activeCompetitionLabel} timing
                </p>
                {isLoadingGameweeks ? (
                  <p className="matchday-timing-text">
                    Loading matchday timing...
                  </p>
                ) : matchdayTiming ? (
                  <div className="matchday-timing-grid">
                    <div className="matchday-timing-row">
                      <div className="matchday-timing-heading">
                        <span className="matchday-badge">
                          {matchdayTiming.currentBadge}
                        </span>
                      </div>

                      <div className="matchday-timing-label">
                        {`Kickoff: `}
                        <span className="matchday-timing-text">
                          {formatDateTime(matchdayTiming.currentFirstTime)}
                        </span>
                        {matchdayTiming.currentDeadline && (
                          <span className="matchday-deadline-text">
                            {` DL: ${formatTime(matchdayTiming.currentDeadline)}`}
                          </span>
                        )}
                      </div>

                      <div className="matchday-timing-label">
                        {`Last Kickoff: `}
                        <span className="matchday-timing-text">
                          {formatDateTime(matchdayTiming.currentTime)}
                        </span>
                      </div>
                    </div>

                    {matchdayTiming.nextTime ? (
                      <div className="matchday-timing-row">
                        <div className="matchday-timing-heading">
                          <span className="matchday-badge">
                            {matchdayTiming.nextBadge}
                          </span>
                        </div>
                        <div className="matchday-timing-label">
                          {`${matchdayTiming.nextLabel}: `}
                          <span className="matchday-timing-text">
                            {formatDateTime(matchdayTiming.nextTime)}
                          </span>
                        </div>
                        <p className="matchday-countdown">
                          {matchdayTiming.nextCountdown}
                        </p>
                      </div>
                    ) : (
                      <p className="matchday-timing-text between-seasons-text">
                        New season coming soon
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="matchday-timing-text between-seasons-text">
                    New season coming soon — check back when fixtures are available.
                  </p>
                )}
              </div>
            </section>
          </>
        ) : (
          <p>Loading profile...</p>
        )}

        {profile?.id && activeCompetitionId !== null ? (
          <Predictions
            username={profile.id}
            leaguesRefreshSignal={leaguesRefreshSignal}
            activeCompetitionId={activeCompetitionId}
            onCompetitionChange={setActiveCompetitionId}
          />
        ) : (
          <p>Loading predictions...</p>
        )}

        <footer className="app-footer">
          <span>&copy; 2026 Match Mixer</span>
          <span className="legal-footer-sep">·</span>
          <a href={`mailto:${SUPPORT_EMAIL}`}>Contact Support</a>
          <span className="legal-footer-sep">·</span>
          <button
            className="legal-footer-btn"
            onClick={() => setLegalPage("privacy")}
          >
            Privacy Policy
          </button>
          <span className="legal-footer-sep">·</span>
          <button
            className="legal-footer-btn"
            onClick={() => setLegalPage("terms")}
          >
            Terms of Service
          </button>
        </footer>
        {legalPage && (
          <Legal page={legalPage} onClose={() => setLegalPage(null)} />
        )}
      </main>
      {showBackToTop && (
        <button
          className="back-to-top"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          aria-label="Back to top"
        >
          &#8679;
        </button>
      )}
      <ManageLeaguesModal
        isOpen={manageLeaguesOpen}
        onClose={() => {
          setManageLeaguesOpen(false);
          setLeaguesRefreshSignal((n) => n + 1);
        }}
        isFirstSetup={isFirstSetupModal}
      />
    </div>
  );
  // #endregion
}

// #region AppContent — auth-aware shell: tracks Sentry user identity, shows a loading spinner while Firebase resolves, renders the Auth screen for signed-out users, or the main App for signed-in users
function AppContent() {
  const { user, loading } = useAuth();

  useEffect(() => {
    setSentryUser(user);
  }, [user]);

  if (loading) {
    return (
      <div className="App">
        <main className="app-main">
          <p>Loading...</p>
        </main>
      </div>
    );
  }

  if (!user) {
    return <Auth />;
  }

  return <App />;
}
// #endregion

// #region App Wrapper: Auth-gated app shell composition.
function AppWrapper() {
  return (
    <AuthProvider>
      <LoadingProvider>
        <AppContent />
      </LoadingProvider>
    </AuthProvider>
  );
}
// #endregion

// #region Exports: Default component export for app entry.
export default AppWrapper;
// #endregion
